import mqtt, { MqttClient } from 'mqtt';

let sharedMqttClient: MqttClient | null = null;
let runtimeMqttBrokerUrl = '';
let runtimeMqttUsername = '';
let runtimeMqttPassword = '';
let resolvedBrokerUrl = '';
let hasWarnedInsecureWsOnHttps = false;
const MQTT_CONNECTION_ENABLED_KEY = 'settings:MQTT_CONNECTION_ENABLED';
const MQTT_DEFAULT_WS_PATH = '/mqtt';
const MQTT_SSL_TLS_ENABLED = true;
const MQTT_SSL_SECURE = true;

const hasProtocolPrefix = (value: string): boolean => /^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(value);

const getPreferredWsProtocol = (): 'ws:' | 'wss:' => {
  return isHttpsPage() ? 'wss:' : 'ws:';
};

const isZeroPrefixedIpv4 = (hostname: string): boolean => /^0\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname);

const isHttpsPage = (): boolean => {
  return typeof window !== 'undefined' && window.location.protocol === 'https:';
};

const isInsecureWsUrl = (url: string): boolean => {
  return /^ws:\/\//i.test(url);
};

const shouldBlockWsOverHttps = (url: string): boolean => {
  return isHttpsPage() && isInsecureWsUrl(url);
};

const warnWsBlockedOnHttps = (): void => {
  if (hasWarnedInsecureWsOnHttps) {
    return;
  }

  hasWarnedInsecureWsOnHttps = true;
  console.warn('[MQTT] WS connection blocked: browser does not allow ws:// from an HTTPS page. Use HTTP app origin for ws-only mode.');
};

const normaliseAndValidateBrokerUrl = (url: string): string => {
  const trimmedUrl = url.trim();
  if (!trimmedUrl) {
    return '';
  }

  const withProtocol = hasProtocolPrefix(trimmedUrl) ? trimmedUrl : `${getPreferredWsProtocol()}//${trimmedUrl}`;
  const wsNormalised = normaliseToWebSocketProtocol(withProtocol);

  try {
    const parsedBrokerUrl = new URL(wsNormalised);
    const protocol = parsedBrokerUrl.protocol;

    if (protocol !== 'ws:' && protocol !== 'wss:') {
      console.warn(`[MQTT] Unsupported protocol in broker URL: ${protocol}`);
      return '';
    }

    if (!parsedBrokerUrl.hostname) {
      console.warn('[MQTT] Missing hostname in broker URL.');
      return '';
    }

    if (isZeroPrefixedIpv4(parsedBrokerUrl.hostname)) {
      console.warn(`[MQTT] Invalid broker hostname (starts with 0.): ${parsedBrokerUrl.hostname}`);
      return '';
    }

    const path =
      parsedBrokerUrl.pathname && parsedBrokerUrl.pathname !== '/'
        ? parsedBrokerUrl.pathname
        : MQTT_DEFAULT_WS_PATH;

    return `${protocol}//${parsedBrokerUrl.hostname}${parsedBrokerUrl.port ? `:${parsedBrokerUrl.port}` : ''}${path}`;
  } catch {
    console.warn('[MQTT] Malformed broker URL.');
    return '';
  }
};

const readMqttConnectionEnabledFlag = (): boolean => {
  if (typeof window === 'undefined') {
    return true;
  }

  const rawValue = localStorage.getItem(MQTT_CONNECTION_ENABLED_KEY);
  if (rawValue == null) {
    return true;
  }

  return rawValue === 'true';
};

const writeMqttConnectionEnabledFlag = (enabled: boolean): void => {
  if (typeof window === 'undefined') {
    return;
  }

  localStorage.setItem(MQTT_CONNECTION_ENABLED_KEY, JSON.stringify(enabled));
};

const normaliseToWebSocketProtocol = (url: string): string => {
  return url
    .replace(/^mqtts:\/\//i, 'wss://')
    .replace(/^mqtt:\/\//i, 'ws://');
};

const applyTlsProtocolPreference = (url: string): string => {
  if (isHttpsPage()) {
    return url.replace(/^ws:\/\//i, 'wss://');
  }

  if (MQTT_SSL_TLS_ENABLED) {
    return url.replace(/^ws:\/\//i, 'wss://');
  }

  return url.replace(/^wss:\/\//i, 'ws://');
};

const resolveBrokerPath = (url: string): string => {
  try {
    const parsedUrl = new URL(url);
    return parsedUrl.pathname && parsedUrl.pathname !== '/' ? parsedUrl.pathname : MQTT_DEFAULT_WS_PATH;
  } catch {
    return MQTT_DEFAULT_WS_PATH;
  }
};

const applyBrokerUrlToClientOptions = (client: MqttClient, brokerUrl: string): void => {
  try {
    const parsedBrokerUrl = new URL(brokerUrl);
    const protocol = parsedBrokerUrl.protocol.replace(':', '');
    const hostname = parsedBrokerUrl.hostname;
    const port = parsedBrokerUrl.port
      ? Number(parsedBrokerUrl.port)
      : protocol === 'wss'
      ? 443
      : 80;
    const path = parsedBrokerUrl.pathname || MQTT_DEFAULT_WS_PATH;

    const options = client.options as any;
    options.protocol = protocol;
    options.hostname = hostname;
    options.host = hostname;
    options.port = port;
    options.path = path;
    options.href = brokerUrl;
    options.servers = [{ protocol, host: hostname, port }];
  } catch {
    // Ignore malformed URLs and keep current connection options unchanged.
  }
};

const applyBrokerCredentialsToClientOptions = (client: MqttClient): void => {
  const options = client.options as any;
  options.username = runtimeMqttUsername || undefined;
  options.password = runtimeMqttPassword || undefined;
};

const refreshSharedClientBrokerUrl = (brokerUrl: string): void => {
  const effectiveBrokerUrl = applyTlsProtocolPreference(brokerUrl);

  if (shouldBlockWsOverHttps(effectiveBrokerUrl)) {
    warnWsBlockedOnHttps();
    return;
  }

  if (!sharedMqttClient || !effectiveBrokerUrl || !resolvedBrokerUrl || effectiveBrokerUrl === resolvedBrokerUrl) {
    return;
  }

  applyBrokerUrlToClientOptions(sharedMqttClient, effectiveBrokerUrl);
  resolvedBrokerUrl = effectiveBrokerUrl;
  if (sharedMqttClient.connected) {
    sharedMqttClient.end(true);
  }
  sharedMqttClient.reconnect();
};

const refreshSharedClientCredentials = (): void => {
  if (!sharedMqttClient) {
    return;
  }

  applyBrokerCredentialsToClientOptions(sharedMqttClient);
  if (sharedMqttClient.connected) {
    sharedMqttClient.end(true);
  }
  sharedMqttClient.reconnect();
};

export const buildMqttBrokerUrl = (serverUrl?: string): string => {
  const explicitBrokerUrl = runtimeMqttBrokerUrl;

  if (explicitBrokerUrl) {
    const normalisedBrokerUrl = normaliseAndValidateBrokerUrl(explicitBrokerUrl);
    if (!normalisedBrokerUrl) {
      return '';
    }

    runtimeMqttBrokerUrl = normalisedBrokerUrl;
    return normalisedBrokerUrl;
  }

  if (!serverUrl) {
    return '';
  }

  try {
    const parsedServerUrl = new URL(serverUrl);
    const protocol = getPreferredWsProtocol();
    return `${protocol}//${parsedServerUrl.host}${MQTT_DEFAULT_WS_PATH}`;
  } catch {
    return '';
  }
};

export const setRuntimeMqttBrokerUrl = (url?: string): void => {
  runtimeMqttBrokerUrl = typeof url === 'string' ? normaliseAndValidateBrokerUrl(url) : '';

  if (!readMqttConnectionEnabledFlag()) {
    return;
  }

  const nextBrokerUrl = buildMqttBrokerUrl();
  refreshSharedClientBrokerUrl(nextBrokerUrl);
};

export const setRuntimeMqttCredentials = (username?: string, password?: string): void => {
  runtimeMqttUsername = typeof username === 'string' ? username.trim() : '';
  runtimeMqttPassword = typeof password === 'string' ? password : '';

  if (!readMqttConnectionEnabledFlag()) {
    return;
  }

  refreshSharedClientCredentials();
};

export const isMqttConnectionEnabled = (): boolean => {
  return readMqttConnectionEnabledFlag();
};

export const setMqttConnectionEnabled = (enabled: boolean, serverUrl?: string): MqttClient | null => {
  writeMqttConnectionEnabledFlag(enabled);

  if (!enabled) {
    if (sharedMqttClient) {
      sharedMqttClient.end(true);
    }
    return sharedMqttClient;
  }

  if (sharedMqttClient) {
    const nextBrokerUrl = buildMqttBrokerUrl(serverUrl);
    refreshSharedClientBrokerUrl(nextBrokerUrl);

    if (!sharedMqttClient.connected) {
      sharedMqttClient.reconnect();
    }
    return sharedMqttClient;
  }

  return getSharedMqttClient(serverUrl);
};

export const getSharedMqttClient = (serverUrl?: string): MqttClient | null => {
  if (!readMqttConnectionEnabledFlag()) {
    return null;
  }

  if (sharedMqttClient) {
    return sharedMqttClient;
  }

  const brokerUrl = buildMqttBrokerUrl(serverUrl);
  const effectiveBrokerUrl = applyTlsProtocolPreference(brokerUrl);
  const brokerPath = resolveBrokerPath(effectiveBrokerUrl);
  if (!brokerUrl) {
    return null;
  }

  if (shouldBlockWsOverHttps(effectiveBrokerUrl)) {
    warnWsBlockedOnHttps();
    return null;
  }

  resolvedBrokerUrl = effectiveBrokerUrl;

  const effectiveProtocol = (() => {
    try {
      const parsed = new URL(effectiveBrokerUrl);
      return parsed.protocol === 'wss:' ? 'wss' : 'ws';
    } catch {
      return MQTT_SSL_TLS_ENABLED ? 'wss' : 'ws';
    }
  })();

  const mqttClientId = `smartlocator_${Math.random().toString(16).slice(2, 10)}`;

  sharedMqttClient = mqtt.connect(effectiveBrokerUrl, {
    clientId: mqttClientId,
    protocol: effectiveProtocol,
    username: runtimeMqttUsername || undefined,
    password: runtimeMqttPassword || undefined,
    rejectUnauthorized: MQTT_SSL_SECURE,
    path: brokerPath,
    clean: true,
    reconnectPeriod: 5000,
    connectTimeout: 4000,
  });

  return sharedMqttClient;
};

export const parseMqttMessage = (rawMessage: string): string => {
  try {
    const payload = JSON.parse(rawMessage);
    return payload?.message ?? payload?.info ?? payload?.description ?? rawMessage;
  } catch {
    return rawMessage;
  }
};
